import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../Components/Themed";
import type { FlashMarketFeeProps, LocationProps, MyPlayerProps, PollCampaignProps, PollOptionProps, PollProps, PollResponseProps, PollSummaryProps } from '../types';
import { FlashAdminApi, FlashMarketApi, FlashMarketHelpers } from './api';
import { Checkbox, Icons } from '../Components';
import { useColors } from '../constants/useColors';
import { ActivityIndicator, Alert, FlatList, Image, type ViewStyle } from 'react-native';
import FlashMarketHelp from './components/FlashMarketHelp';
import moment from 'moment-mini';
import MarketTimer from './components/MarketTimer';
import { MarketComponentHelpers } from '../MarketComponents/api';

type FlashMarketProps = {
    loading:boolean,
    poll?:PollProps,
    poll_campaign?:PollCampaignProps,
    poll_options:PollOptionProps[],
    poll_summaries:PollSummaryProps[],
    flash_market_fees:FlashMarketFeeProps[],
    hide_image?:boolean,
    location?:LocationProps,
    wrap_options?:boolean,
    card_style?:any,
    onManageFlash?:(poll:PollProps) => void,
    init_expanded?:boolean,
    hide_timer?:boolean,
    onReload:() => void,
    header_style?:ViewStyle,
    hide_footer?:boolean,
    footer_style?:ViewStyle,
    player_id?:string
    player?:MyPlayerProps,
    onRequestLocation:() => void,
    onFocusPosition?: (position:{ x:number, y:number, width:number, height:number }) => void,
    onRequestAuthenticate: () => void
}
const MIN_OPTION_WIDTH = 300
const FlashMarket = ({ loading, poll, hide_footer, hide_timer, wrap_options, poll_options, poll_summaries, poll_campaign, flash_market_fees, hide_image, init_expanded, card_style, header_style, footer_style, onReload, location, player, player_id, onManageFlash, onRequestAuthenticate, onFocusPosition, onRequestLocation }:FlashMarketProps) => {
    const Colors = useColors();
    const [ size, setSize ] = useState({ width:0, height:0 });
    const [ admin_action, setAdminActions ] = useState<{
        selected_option_id?:string,
        admin_loading:boolean
    }>({
        admin_loading:false
    });
    const { selected_option_id, admin_loading } = admin_action;
    const [ expanded, setExpanded ] = useState(false);
    const [ show_help, setShowHelp ] = useState(false);
    const [ action_loading, setActionLoading ] = useState(false);
    const [ draft_data, setDraftData ] = useState<{
        stake: string
    }>({
        stake: '0'
    })

    const cl = poll_campaign?.market_type == 'FOR_MONEY' ? '$' : 'E'
    const [ my_data, setMyData ] = useState<{
        me_loading:boolean,
        my_responses:PollResponseProps[]
    }>({
        me_loading:false,
        my_responses:[]
    });
    const { me_loading, my_responses } = my_data;
    const [ selected_option, setSelectedOption ] = useState<PollOptionProps | undefined>(undefined);
    const draft_stake = FlashMarketHelpers.calcDraftStake(my_responses, draft_data.stake, selected_option?.poll_option_id);
    const potential_winnings = FlashMarketHelpers.calcPotentialWinnings(selected_option?.parimutuel_odds, draft_stake.toFixed(2));
    const order_valid = FlashMarketHelpers.isValid(my_responses, poll, draft_data.stake, selected_option?.poll_option_id);
    const visible_options = poll_options.filter(po => po.status != 'inactive').sort((a,b) => a.priority - b.priority)
    const num_options_per_row = Math.floor((size.width-20) / MIN_OPTION_WIDTH)
    
    const option_width = ((size.width-20) / num_options_per_row) - (4*num_options_per_row)
    const is_inactive = poll && ['pending','paused'].includes(poll?.status) ? true : false
    const is_upcoming = poll && poll.start_datetime && moment().isBefore(moment(poll.start_datetime)) ? true : false
    const is_admin = player?.role == 'admin' ? true : poll?.admins && poll.admins.includes(player?.player_id ?? '0') ? true : false
    useEffect(() => {
        if(!poll){ return }
        FlashMarketApi.setEnvironment();
         setDraftData({ ...draft_data, stake: poll.minimum_stake.toFixed(2) });
        if(init_expanded){ setExpanded(true) }
        if(poll.winning_option_id){ setAdminActions({ ...admin_action, selected_option_id:poll.winning_option_id }) }
    },[poll]);

    useEffect(() => {
        if(!poll || !player_id){ return }
        loadMyData();
    },[poll, player_id]);

    const isResolveable = () => {
        if(!poll){ return false }
        if(!player){ return false }
        if(!is_admin){ return false }
        if(!['paused','active'].includes(poll.status)){ return false }
        if(!poll.winning_option_id){ return false }
        if(poll.winning_option_id != selected_option_id){ return false }
        return true
    }
    const resolveable = isResolveable();

    const handleSubmit = async(poll_option:PollOptionProps) => {
        if(!player_id){ return onRequestAuthenticate() }
        if(!order_valid){ return }
        if(action_loading){ return }
        if(!poll_campaign){ return }
        if(poll_campaign.market_type == 'FOR_MONEY'){
            if(!location){ return onRequestLocation() }
            if(!location.legal_ind){ return alert(`Real money flash markets are not available in your current location`) }
        }
        setActionLoading(true);
        let previous_response = my_responses.find(r => r.poll_option_id == poll_option.poll_option_id);
        let stake_num = parseFloat(draft_data.stake);
        const response = await FlashMarketApi.createPMOrder(poll_option.poll_option_id, stake_num, previous_response?.poll_response_id);
        if(!response){ return alert('Unable to process order') }
        setMyData({
            ...my_data,
            my_responses: my_responses.filter(r => r.poll_response_id != response.poll_response.poll_response_id).concat(response.poll_response)
        });
        setSelectedOption(undefined);
        setActionLoading(false)
    }

    const handleResolve = async() => {
        if(!resolveable){ return }
        if(admin_loading){ return }
        if(!poll){ return }
        const winning_option = poll_options.find(po => po.poll_option_id == poll.winning_option_id)
        if(!winning_option){ return }
        if(winning_option){
            await Alert.alert(`Are you sure?`,  `I confirm that ${winning_option.option_name} is the correct answer.`, [
                {
                    style: 'cancel',
                    text: 'Cancel',
                    onPress: () => console.log('')
                },
                {
                    style: 'default',
                    text: 'Confirm',
                    onPress: async() => {
                        setAdminActions({ ...admin_action, admin_loading: true });
                        await FlashAdminApi.closePoll(poll.poll_id, winning_option.poll_option_id)
                        setAdminActions({ ...admin_action, admin_loading: false });
                    }
                }
            ])
        }
    }

    const handleSubmitWinningOption = async(poll_option_id?:string) => {
        if(!poll){ return alert('Unable to process') }
        if(admin_loading){ return }
        setAdminActions({ ...admin_action, admin_loading: true });
        await FlashAdminApi.updatePoll({ ...poll, winning_option_id: poll_option_id });
        setAdminActions({ ...admin_action, admin_loading: false, selected_option_id: undefined });
    }

    const generateResultIcon = (option:PollOptionProps) => {
        switch(option.result_ind){
            case 'win':
                return (
                    <Icons.CheckIcon color={Colors.text.success} size={18} />
                )
            case 'lose':
                return (
                    <Icons.CloseIcon color={Colors.text.error} size={14} />
                )
            case 'draw':
                return (
                    <Icons.AlertIcon color={Colors.text.h1} size={18}/>
                )
            default: return <></>
        }
    }

    const generateStatusIcon = (poll:PollProps) => {
        switch(poll.status){
            case 'active': return <Icons.ActivityIcon size={18} color={Colors.text.success} />
            case 'paused': return <Icons.PausedIcon size={18} color={Colors.text.h1} />
            case 'closed': return <Icons.LockClosedIcon size={18} color={Colors.text.h1}/>
            default: return <Icons.AlertIcon size={18} color={Colors.text.warning} />
        }
    }

    const getColor = (number:number) => {
        if(number > 0){ return Colors.text.success }
        if(number < 0){ return Colors.text.error }
        return Colors.text.h1
    }

    const loadMyData = async() => {
        if(!poll){ return }
        setMyData({ ...my_data, me_loading: true });
        let responses = await FlashMarketApi.getMyCampaignResponses(poll.poll_campaign_id);
        let poll_responses = responses.filter(r => r.poll_id == poll.poll_id);
        setMyData({ ...my_data, me_loading: false, my_responses:poll_responses })
    }

    const isOrderAllowed = (option:PollOptionProps, my_responses:PollResponseProps[]) => {
        if(!poll){ return false }
        if(poll.winning_option_id){ return false }
        if(poll.status != 'active'){ return false }
        let my_response = my_responses.find(r => r.poll_option_id == option.poll_option_id);
        if(!my_response && my_responses.length > 0 && !poll.multi_responses_allowed){ return false }
        if(my_response && !poll.stake_increase_allowed){ return false }
        return true
    }

    const renderOptions = (data:{ item:PollOptionProps, index:number }) => {
        const my_option = my_responses.find(r => r.poll_option_id == data.item.poll_option_id)
        const allow_order = isOrderAllowed(data.item, my_responses);
        const response_stats = FlashMarketHelpers.getResponseStats(data.item, my_option);
        const selected = selected_option?.poll_option_id == data.item.poll_option_id ? true : false
        const admin_checked = selected_option_id == data.item.poll_option_id ? true : false
        return (
            <View float style={{ width:option_width, margin:2, borderRadius:8,borderWidth:my_option?1:0, borderColor:Colors.text.action, padding:10  }}>

            <View transparent key={data.item.poll_option_id.toString()} style={{ flexDirection:'row', alignItems:'center' }}>
                {!selected && poll?.status != 'closed' && is_admin ?
                <Checkbox
                    style={{ marginRight:5 }}
                    checked={admin_checked}
                    onSelect={() => {
                        if(admin_checked){ setAdminActions({ ...admin_action, selected_option_id: undefined }) }
                        else { setAdminActions({ ...admin_action, selected_option_id: data.item.poll_option_id }) }
                    }}
                />
                :<></>}
                {my_option ?
                <View transparent style={{ marginRight:10 }}>
                    {player ?
                    <Image
                        source={{ uri: player.profile_pic && player.profile_pic != '' ? player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                        style={{ height:30, width:30, borderRadius:4 }}
                        resizeMode='cover'
                    />
                    :
                    <Icons.UserIcon color={Colors.text.action} size={14} />
                    }
                </View>
                :<></>}
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1'>{data.item.option_name}</Text>
                    {selected && data.item.parimutuel_odds && data.item.parimutuel_odds > 0 ?
                    <Text theme='h2' style={{ marginTop:3 }} color={Colors.text.action}>${draft_stake.toFixed(2)} to win ${potential_winnings.toFixed(2)}*</Text>
                    :<></>}
                </View>
                {!selected ?
                <View transparent style={{ marginRight:10, marginLeft:10, alignItems:'flex-end' }}>
                    <View transparent style={{ alignItems:'flex-end' }}>
                        <Text theme='h2'>{((data.item.parimutuel_odds ?? 0)*100).toFixed(2)}%</Text>
                        <Text theme='description' style={{ marginTop:3 }}>{MarketComponentHelpers.getOddsLabel(MarketComponentHelpers.calcAmericanOddsFromProbability(data.item.parimutuel_odds ?? 0))}</Text>
                    </View>
                </View>
                :<></>}
                {selected ?
                <View transparent>
                    <Button
                        title='X'
                        style={{ padding:10, marginLeft:10 }}
                        transparent
                        title_color={Colors.text.error}
                        onPress={() => setSelectedOption(undefined)}
                    />
                </View>
                :
                <View transparent>
                    {allow_order ?
                    <Button
                        type='action'
                        disabled={action_loading}
                        style={{ padding:10 }}
                        onPress={() => setSelectedOption(data.item)}
                    >
                        <Text theme='h2' color={Colors.text.white}>{my_option ? 'ADD' : 'BUY'}</Text>
                    </Button>
                    :<></>}
                </View>
                }
                {data.item.result_ind ?
                <View transparent style={{ padding:10 }}>
                    {generateResultIcon(data.item)}
                </View>
                :<></>}
            </View>
            {selected ?
            <View transparent style={{ flexDirection:'row', alignItems:'center', marginTop:10 }}>
                <TextInput
                    onFocusPosition={onFocusPosition}
                    onChangeText={(text) => setDraftData({ ...draft_data, stake: text })}
                    style={{ flex:1, textAlign:'center', padding:10 }}
                    value={draft_data.stake}
                />
                <Button
                    type='success'
                    disabled={!order_valid || action_loading}
                    loading={action_loading}
                    style={{ padding:10, marginLeft:10, opacity:order_valid && !action_loading?1:0.5 }}
                    title='SUBMIT'
                    onPress={() => handleSubmit(data.item)}
                />
            </View>
            :my_option?.result_ind ? 
                <Text style={{ marginTop:3 }} theme='h2' color={getColor(response_stats.earnings)}>{cl}{response_stats.earnings.toFixed(2)} Earned</Text>
            :my_option ?
                <Text style={{ marginTop:3 }} theme='h2' color={Colors.text.action}>{cl}{response_stats.stake.toFixed(2)} to win {cl}{response_stats.potential_winnings.toFixed(2)}*</Text>
            :<></>
            }
            
            </View>
        )
    }

    if(!poll){ return <></> }
    //if(!poll.flash_active){ return <></> }
    const stats = FlashMarketHelpers.getStats(poll_summaries, flash_market_fees);
    return (
        <View transparent style={{ ...card_style }} onLayout={(ev) => {
            const { width, height } = ev.nativeEvent.layout;
            setSize({ width, height });
        }}>
            <Button 
            style={{ backgroundColor:Colors.views.header, padding:10, flexDirection:'row', alignItems:'center', ...header_style }}
            onPress={() => setExpanded(!expanded)}>
                {!hide_image && poll.poll_image ?
                <Image
                    source={{ uri: poll.poll_image?.url }}
                    style={{ height:50, width:50 }}
                    resizeMode='cover'
                />
                : !hide_image ?
                <Icons.FlameIcon color={Colors.text.warning} size={24} />
                :<></>}
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='light' size={14}>🔥 Flash Market 🔥</Text>
                    <Text theme='h1' style={{ marginTop:4 }}>{poll.poll_question}</Text>
                </View>
                {generateStatusIcon(poll)}
                <Button
                    transparent
                    disabled
                    onPress={() => console.log('')}
                >
                    <Icons.ChevronIcon direction={expanded ? 'up' : 'down'} size={8} color={Colors.text.action} />
                </Button>
            </Button>
            <View transparent type='body'>
                {show_help ?
                <View transparent>
                    <FlashMarketHelp />
                </View>
                :
                <View transparent>
                    {expanded ?
                    <View transparent type='body' style={{ padding:10 }}>
                        {!hide_timer && !is_upcoming && poll.status == 'active' ?
                        <MarketTimer
                            poll={poll}
                            onTimesUp={() => onReload()}
                        />
                        :<></>}
                        {wrap_options ?
                        <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                            {visible_options.map((o,i) => {
                                return renderOptions({ item:o,index:i })
                            })}
                        </View>
                        :
                        <FlatList
                            data={visible_options}
                            key={`poll_${poll.poll_id}_options`}
                            renderItem={renderOptions}
                            keyExtractor={(item) => item.poll_option_id.toString()}
                        />
                        }
                        {is_inactive && !is_admin ?
                        <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                            <View float style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                                <Icons.PausedIcon size={35} color={Colors.text.h1} />
                                <View transparent style={{ marginLeft:20 }}>
                                    <Text theme='h1'>Market Is Inactive</Text>
                                </View>
                            </View>
                        </View>
                        :is_upcoming ?
                        <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                            <View float style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                                <Icons.LockClosedIcon size={30} color={Colors.text.h1} />
                                <View transparent style={{ marginLeft:20 }}>
                                    <Text theme='h1'>Market Opens Soon!</Text>
                                    {poll.start_datetime ?
                                    <Text theme='h2' color={Colors.text.success} style={{ marginTop:4 }}>Market opens at {moment(poll.start_datetime).format('MMM DD hh:mm a')}</Text>
                                    :<></>}
                                </View>
                            </View>
                        </View>
                        :<></>}
                        {loading || me_loading ?
                        <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                            <ActivityIndicator size={'large'} color={Colors.text.h1} />
                        </View>
                        :<></>}
                    </View>
                    :<></>}
                </View>
                }
            </View>
            {is_admin ?
            <View type='footer' style={{ flexDirection:'row', padding:5 }}>
                {onManageFlash ?
                <Button float style={{ margin:5, padding:16 }} onPress={() => onManageFlash(poll)}>
                    <Icons.SettingsIcon size={16} color={Colors.text.h1} />
                </Button>
                :<></>}
                {player?.role =='admin' && poll.flash_active ?
                <Button
                    title={`Unflash`}
                    style={{ flex:0.5, margin:4 }}
                    disabled={admin_loading}
                    loading={admin_loading}
                    backgroundColor={Colors.text.warning}
                    title_color={Colors.text.white}
                    onPress={async() => {
                        if(!poll){ return }
                        setAdminActions({ ...admin_action, admin_loading: true });
                        await FlashAdminApi.updatePoll({ ...poll, flash_active: false });
                        setAdminActions({ ...admin_action, admin_loading: false });
                    }}
                />
                :['active','paused'].includes(poll.status) && player?.role == 'admin' ?
                <Button
                    title={`Flash`}
                    style={{ flex:0.5, margin:4 }}
                    disabled={admin_loading}
                    loading={admin_loading}
                    backgroundColor={Colors.text.success}
                    title_color={Colors.text.white}
                    onPress={async() => {
                        if(!poll){ return }
                        setAdminActions({ ...admin_action, admin_loading: true });
                        await FlashAdminApi.updatePoll({ ...poll, flash_active: true });
                        setAdminActions({ ...admin_action, admin_loading: false });
                    }}
                />
                :<></>}
                {poll.status == 'active' && !poll.winning_option_id ?
                <Button
                    style={{ flex:1, margin:4, flexDirection:'row', alignItems:'center', justifyContent:'center' }}
                    disabled={admin_loading}
                    loading={admin_loading}
                    backgroundColor={Colors.text.error}
                    title_color={Colors.text.white}
                    onPress={async() => {
                        if(!poll){ return }
                        setAdminActions({ ...admin_action, admin_loading: true });
                        await FlashAdminApi.updatePoll({ ...poll, status: 'paused' });
                        setAdminActions({ ...admin_action, admin_loading: false });
                    }}
                >
                    <Icons.PauseIcon size={10} color={Colors.text.white} />
                    <Text style={{ marginLeft:5 }} theme='h2' color={Colors.text.white}>Pause</Text>
                </Button>
                :<></>}
                {poll.status == 'paused' ?
                <Button
                    style={{ flex:1, margin:4, flexDirection:'row', alignItems:'center', justifyContent:'center' }}
                    disabled={admin_loading}
                    loading={admin_loading}
                    type='success'
                    onPress={async() => {
                        if(!poll){ return }
                        setAdminActions({ ...admin_action, admin_loading: true });
                        await FlashAdminApi.updatePoll({ ...poll, status: 'active' });
                        setAdminActions({ ...admin_action, admin_loading: false });
                    }}
                >
                    <Icons.PlayIcon size={10} color={Colors.text.white} />
                    <Text style={{ marginLeft:5 }} theme='h2' color={Colors.text.white}>Resume</Text>
                </Button>
                :<></>}
                {selected_option_id != poll.winning_option_id ?
                <Button
                    title={`Grade Flash Market`}
                    style={{ flex:1, margin:4 }}
                    disabled={admin_loading}
                    loading={admin_loading}
                    type='success'
                    onPress={() => handleSubmitWinningOption(selected_option_id)}
                />
                :<></>}
                {resolveable ?
                <Button
                    title={'Resolve and Close'}
                    style={{ flex:1, margin:3 }}
                    disabled={admin_loading}
                    loading={admin_loading}
                    backgroundColor={Colors.text.warning}
                    title_color={Colors.text.white}
                    onPress={() => handleResolve()}
                />
                :<></>}
            </View>
            :<></>}
            {expanded && !hide_footer ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, ...footer_style }}>
                <Button transparent style={{ justifyContent:'center', alignItems:'center', borderRadius:0, padding:5, paddingRight:10, marginRight:10, borderRightWidth:1, borderColor:Colors.borders.light }}
                onPress={() => setShowHelp(!show_help)}>
                    <Icons.AlertIcon color={show_help?Colors.text.error:Colors.text.warning} size={18} />
                    <Text style={{ marginTop:2 }} theme='description' color={show_help?Colors.text.error:Colors.text.warning} textAlign='center'>{show_help?'CLOSE':'HELP'}</Text>
                </Button>
                <View transparent style={{ justifyContent:'center', alignItems:'center', padding:5, paddingRight:10, marginRight:10, borderRightWidth:1, borderColor:Colors.borders.light }}>
                    <Text theme='h1' color={Colors.text.action} textAlign='center'>${(poll.minimum_stake).toFixed(2)}</Text>
                    <Text style={{ marginTop:2 }} theme='description' textAlign='center'>MIN STAKE</Text>
                </View>
                <View transparent style={{ justifyContent:'center', alignItems:'center', padding:5, paddingRight:10, marginRight:10, borderRightWidth:1, borderColor:Colors.borders.light }}>
                    <Text theme='h1' color={Colors.text.action} textAlign='center'>${(poll.max_stake).toFixed(2)}</Text>
                    <Text style={{ marginTop:2 }} theme='description' textAlign='center'>MAX STAKE</Text>
                </View>
                <View style={{ flex:1 }} />
                {!is_upcoming ?
                <View transparent style={{ justifyContent:'center', alignItems:'center', padding:5 }}>
                    <Text theme='h1' color={Colors.text.success} textAlign='center'>${stats.total_staked.toFixed()}</Text>
                    <Text style={{ marginTop:2 }} theme='description' textAlign='center'>EST. TOTAL</Text>
                </View>
                :<></>}
            </View>
            :<></>}
            
        </View>
    )
}

export default FlashMarket